import React, { type FC } from 'react'
import { t } from 'i18next'
import ReactJson from 'react-json-view'
import { type RouteComponentProps, useParams, navigate } from '@reach/router'
import { ContentContainer } from '@awell/ui-kit/components/ContentContainer'
import { ScrollContainer } from '@awell/ui-kit/components/ScrollContainer'
import { format } from 'date-fns'
import { isEmpty, isNil } from 'lodash'
import { Button, Icon, Tooltip } from '@awell-health/design-system'
import { ApiCallDetailsSkeleton } from './skeleton'
import { useGetPathwayApiCall } from '../../../hooks/useGetPathwayApiCall'
import { useRetryApiCall } from '../../../hooks/useRetryApiCall'
import { InfoField } from './InfoField'
import { ApiCallResponsesTable } from './ApiCallResponsesTable'
import type { ApiCallStatus } from '../../../generated/types-orchestration'

interface ApiCallDetailsProps extends RouteComponentProps {
  pathwayId: string
}

export const ApiCallDetails: FC<ApiCallDetailsProps> = ({ pathwayId }) => {
  const { id } = useParams()
  const { loading, apiCall } = useGetPathwayApiCall(id as string)

  const { retryApiCall } = useRetryApiCall()

  const backNavButtonUrl = `/pathway/${pathwayId}/logs/api-calls`
  if (loading || !apiCall) {
    return <ApiCallDetailsSkeleton />
  }
  const triggeredAtLastResponse =
    apiCall.responses.length > 0 ? apiCall.responses[apiCall.responses.length - 1].date : ''

  const getApiCallStatusKey = (status: ApiCallStatus) => {
    return `api_call_${status}` as const
  }

  return (
    <ScrollContainer className="bg-white h-full">
      <ContentContainer>
        <article>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button variant="ghost" size={null} shape="SquareLg" onClick={() => navigate(backNavButtonUrl)}>
                <Icon icon="RiArrowLeftLine" size={24} />
              </Button>
              <div className="text-xl">{apiCall?.title}</div>
            </div>
          </div>
          <div className="mx-5 flex flex-col gap-5">
            <div className="flex items-center justify-between mt-5">
              <div className="flex gap-10">
                <InfoField icon="RiGlobalLine" label={t('URL')} value={apiCall.request.endpoint} isCopyable />
                <InfoField
                  icon="RiTimeLine"
                  label={t('triggered_at')}
                  value={
                    isEmpty(triggeredAtLastResponse)
                      ? '--'
                      : format(new Date(triggeredAtLastResponse), 'd-LLL-yyyy K:mm:ss a')
                  }
                />
                <InfoField
                  icon="RiInformationLine"
                  label={t('status')}
                  value={t(getApiCallStatusKey(apiCall.status as ApiCallStatus))}
                />
              </div>
              <Tooltip datatip="Retry">
                <Button
                  variant="secondary"
                  shape="SquareLg"
                  size={null}
                  onClick={() => {
                    retryApiCall(id as string)
                  }}
                >
                  <Icon icon="RiRefreshLine" size={24} />
                </Button>
              </Tooltip>
            </div>

            <section>
              <div className="flex flex-col gap-3">
                <div className="text-lg font-medium">{t('logs')}</div>
                <ApiCallResponsesTable responses={apiCall.responses} />
              </div>
            </section>

            <section>
              <div className="flex flex-col gap-3">
                <div className="text-lg font-medium">{t('request_body')}</div>
                <div className="bg-slate-100 rounded-md p-4">
                  <ReactJson src={!isNil(apiCall?.request?.body) && JSON.parse(apiCall.request.body)} />
                </div>
              </div>
            </section>
          </div>
        </article>
      </ContentContainer>
    </ScrollContainer>
  )
}

ApiCallDetails.displayName = 'ApiCallDetails'
